import { useState } from 'react';
import { Link } from 'react-router-dom';
import { auth } from '../api/auth';
import { Mail, ArrowLeft, Send, CheckCircle } from 'lucide-react';
import { useSeo } from '../utils/seo';

const ForgotPassword = () => {
  useSeo({
    title: 'Reset Your Password',
    description:
      'Request a password reset link for your Nixook shipping console account.',
    index: true,
  });

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await auth.forgotPassword({ email });
      setSent(true);
    } catch {
      // Error handled by global interceptor
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 shadow-xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-indigo-100 flex items-center justify-center mx-auto mb-4">
            <Mail className="text-indigo-600" size={28} />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            {sent ? 'Check Your Email' : 'Forgot Password?'}
          </h1>
          <p className="text-slate-500 text-sm">
            {sent
              ? `We've sent a password reset link to ${email}. Please check your inbox.`
              : "No worries! Enter your email address and we'll send you a reset link."}
          </p>
        </div>

        {sent ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle className="text-green-500 flex-shrink-0" size={20} />
              <p className="text-green-700 text-sm">
                Reset link sent! The link expires in 1 hour.
              </p>
            </div>
            <button
              onClick={() => setSent(false)}
              className="w-full border border-slate-200 text-slate-600 font-medium py-3 rounded-xl hover:bg-slate-50 transition-colors text-sm"
            >
              Resend Email
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                id="forgot-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                placeholder="name@company.com"
                required
              />
            </div>

            <button
              id="forgot-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-colors shadow-lg shadow-indigo-600/20 flex items-center justify-center gap-2"
            >
              {loading ? (
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Send size={16} />
              )}
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
        )}

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="text-indigo-600 hover:text-indigo-700 text-sm font-medium inline-flex items-center gap-1 transition-colors"
          >
            <ArrowLeft size={14} />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
